import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatDialog} from "@angular/material/dialog";
import {ActivatedRoute} from "@angular/router";
import {Store} from "@ngrx/store";
import {Subject, take, takeUntil} from "rxjs";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {AlgorithmicDmvActions} from "../store/algorithmic-dmv.actions";
import {
    selectActiveDetail,
    selectActiveDetailStatus,
    selectIsBulkAccepting,
    selectIsResultPending
} from "../store/algorithmic-dmv.selectors";
import {AlgorithmicDmvJobDetail} from "../generated-sources/api";
import {selectDisplayInMillion} from "../../shared/store/settings/settings.selectors";
import {
    AgentTimingVerdict,
    AgentValueVerdict,
    AlgorithmicDmvAcceptMethod,
    AlgorithmicDmvAgentNarrative,
    AlgorithmicDmvAgentStatus,
    AlgorithmicDmvAlgorithmStatus,
    AlgorithmicDmvAppliedValues,
    AlgorithmicDmvResult,
    AlgorithmicDmvReviewStatus,
    AlgorithmicDmvScenarioValues
} from "../generated-sources/api";

/** A labelled narrative list rendered as a section in the agent panel. */
interface NarrativeSection {
    label: string;
    items: string[];
}

/** Worst-of(value, timing) summary used for the agent panel's tone chip + accent. */
export type AgentTone = "aligned" | "mixed" | "stretched" | "context";

interface ScenarioRow {
    case: "LOW" | "BASE" | "HIGH";
    values: AlgorithmicDmvScenarioValues;
}

/**
 * The read-only "Suggested amendment" view model (US-1159 §6.7). Diffs the agent-suggested BASE
 * multiple against the algorithm's and derives the LOW/HIGH preview by preserving the algorithm's
 * spread multiplicatively (scale = suggestedBase / algorithmBase) — matching the server's apply
 * path, computed here for display only.
 */
export interface SuggestionPreview {
    algorithmBase?: number;
    suggestedBase?: number;
    baseChanged: boolean;
    previewLow?: number;
    previewHigh?: number;
    algorithmExit?: string;
    suggestedExit?: string;
    /** "earlier" | "later" | null — derived by comparing the suggested exit to the algorithm's. */
    movedDirection: "earlier" | "later" | null;
    rationale?: string;
    /**
     * Server-computed: whether the suggestion is a material change (BASE ≥ 0.10x or exit ≥ 3 months).
     * When false the agent agrees with the algorithm and the editable amendment is hidden (US-1159 follow-up).
     */
    material: boolean;
}

@Component({
    selector: "valumize-algorithmic-dmv-viewer-page",
    templateUrl: "./algorithmic-dmv-viewer-page.component.html",
    styleUrls: ["./algorithmic-dmv-viewer-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AlgorithmicDmvViewerPageComponent implements OnInit, OnDestroy {

    private readonly route = inject(ActivatedRoute);
    private readonly store = inject(Store);
    private readonly fb = inject(FormBuilder);

    /**
     * Per-result edit form {baseMultiple, exitDate}, lazily created and memoised by resultId so
     * OnPush change detection reuses the same FormGroup (rebuilding it each cycle would wipe edits).
     */
    private readonly editForms = new Map<number, FormGroup>();

    private readonly dialog = inject(MatDialog);

    readonly detail$ = this.store.select(selectActiveDetail);
    readonly detailStatus$ = this.store.select(selectActiveDetailStatus);
    readonly displayInMillion$ = this.store.select(selectDisplayInMillion);
    readonly bulkAccepting$ = this.store.select(selectIsBulkAccepting);

    // Expose the enums to the template for tag/branch comparisons.
    readonly ALGO_STATUS = AlgorithmicDmvAlgorithmStatus;
    readonly REVIEW_STATUS = AlgorithmicDmvReviewStatus;
    readonly AGENT_STATUS = AlgorithmicDmvAgentStatus;

    taskInfoId!: number;
    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const idStr = params.get("taskInfoId");
            if (!idStr) return;
            this.taskInfoId = Number(idStr);
            this.store.dispatch(AlgorithmicDmvActions.loadDetail({taskInfoId: this.taskInfoId}));
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isPending$(resultId: number) {
        return this.store.select(selectIsResultPending(resultId));
    }

    /** Materialises the {low, base, high} object as a stable-ordered list for the template. */
    scenarioRows(result: AlgorithmicDmvResult): ScenarioRow[] {
        if (!result.scenarios) return [];
        const {low, base, high} = result.scenarios;
        return [
            {case: "LOW",  values: low},
            {case: "BASE", values: base},
            {case: "HIGH", values: high}
        ];
    }

    accept(result: AlgorithmicDmvResult) {
        this.store.dispatch(AlgorithmicDmvActions.acceptResult({
            resultId: result.resultId,
            request: {acceptMethod: AlgorithmicDmvAcceptMethod.Individual},
            taskInfoId: this.taskInfoId
        }));
    }

    skip(result: AlgorithmicDmvResult) {
        this.store.dispatch(AlgorithmicDmvActions.skipResult({
            resultId: result.resultId,
            taskInfoId: this.taskInfoId
        }));
    }

    goBack() {
        history.back();
    }

    /** Pending SUCCESS rows eligible for bulk accept (they carry a suggestion or fall back to the algorithm). */
    pendingAcceptable(detail: AlgorithmicDmvJobDetail): AlgorithmicDmvResult[] {
        return detail.results.filter(r =>
            r.reviewStatus === AlgorithmicDmvReviewStatus.PendingReview
            && r.algorithmStatus === AlgorithmicDmvAlgorithmStatus.Success);
    }

    /**
     * Opens an honest confirm dialog (counts suggestion vs algorithm-fallback; notes bulk uses the
     * stored, un-edited suggestions) then dispatches the bulk accept-all.
     */
    acceptAll(detail: AlgorithmicDmvJobDetail) {
        const acceptable = this.pendingAcceptable(detail);
        const withSuggestion = acceptable.filter(r => !!r.agentNarrative?.suggestion).length;
        const withoutSuggestion = acceptable.length - withSuggestion;
        if (acceptable.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmationComponent, {width: "32rem"});
        dialogRef.componentInstance.confirmMessage = "Accept all suggestions?";
        dialogRef.componentInstance.htmlContent =
            `<p>Apply ${withSuggestion} stored suggestion(s)`
            + (withoutSuggestion > 0 ? `; ${withoutSuggestion} asset(s) have no suggestion and will use the algorithm` : "")
            + ".</p>"
            + "<p>Bulk uses the stored, un-edited suggestions.</p>";
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(AlgorithmicDmvActions.acceptAllSuggestions({taskInfoId: this.taskInfoId}));
            }
        });
    }

    /**
     * The chip displayed in the result-card header summarises the per-asset state
     * the user cares about: agent narrative skipped intentionally here — those land
     * in step 13. So the chip is derived from algorithmStatus + reviewStatus only.
     */
    cardChipLabel(result: AlgorithmicDmvResult): string {
        // Failed / Ineligible outcomes must always surface, even after a later run superseded the
        // row to SKIPPED — otherwise a genuine failure hides behind a benign "Skipped" chip.
        if (result.algorithmStatus === AlgorithmicDmvAlgorithmStatus.Failed) return "Failed";
        if (result.algorithmStatus === AlgorithmicDmvAlgorithmStatus.Ineligible) return "Ineligible";
        if (result.reviewStatus === AlgorithmicDmvReviewStatus.Accepted) return "Accepted";
        if (result.reviewStatus === AlgorithmicDmvReviewStatus.Skipped) return "Skipped";
        return result.algorithmStatus === AlgorithmicDmvAlgorithmStatus.Success ? "Pending review" : "Processing…";
    }

    /**
     * Worst-of(value, timing) summary for the agent panel's tone chip. "context" is the
     * scoped/ineligible case where the agent gives position + market color but no value/timing
     * verdict (both null). Mirrors the methodology's chip-colour = worst-of(value, timing).
     */
    agentTone(narrative: AlgorithmicDmvAgentNarrative): AgentTone {
        if (!narrative.value && !narrative.timing) return "context";
        const valueOff = !!narrative.value && narrative.value.verdict !== AgentValueVerdict.Fair;
        const timingOff = !!narrative.timing && narrative.timing.verdict === AgentTimingVerdict.Questionable;
        if (valueOff && timingOff) return "stretched";
        if (valueOff || timingOff) return "mixed";
        return "aligned";
    }

    /** Humanises an enum token for the summary chip, e.g. "DPI_PRESSURE" → "dpi pressure". */
    humanize(token: string | null | undefined): string {
        return token ? token.replace(/_/g, " ").toLowerCase() : "";
    }

    /**
     * The per-result edit form, created (once) pre-filled from the suggestion: BASE multiple falls
     * back to the algorithm base, exit date to the algorithm's modeled exit. Returns null when the
     * result carries no suggestion.
     */
    suggestionForm(result: AlgorithmicDmvResult): FormGroup | null {
        const s = result.agentNarrative?.suggestion;
        if (!s) return null;
        const existing = this.editForms.get(result.resultId);
        if (existing) return existing;

        const form = this.fb.group({
            baseMultiple: [
                s.baseMultiple ?? result.scenarios?.base?.discountMultiple ?? null,
                [Validators.required, Validators.min(0.0001)]
            ],
            exitDate: [s.exitDate ?? result.scenarios?.modeledExit ?? null, [Validators.required]]
        });
        this.editForms.set(result.resultId, form);
        return form;
    }

    /**
     * The live "Suggested amendment" view model, reading current edit-form values. The LOW/HIGH
     * preview mirrors the server's multiplicative spread (scale = editedBase / algorithmBase); "moved"
     * is derived from the edited exit vs the algorithm's modeled exit (display only — the server is
     * authoritative and re-snaps the date to quarter-end on apply).
     */
    suggestionPreview(result: AlgorithmicDmvResult): SuggestionPreview | null {
        const form = this.suggestionForm(result);
        if (!form) return null;

        const algorithmBase = result.scenarios?.base?.discountMultiple ?? undefined;
        const editedBase = form.value.baseMultiple ?? undefined;
        const baseChanged = editedBase != null && algorithmBase != null && editedBase !== algorithmBase;
        const scale = (baseChanged && algorithmBase && algorithmBase > 0) ? (editedBase as number) / algorithmBase : 1;
        const scaleMaybe = (v?: number) => (v == null ? undefined : v * scale);

        const algorithmExit = result.scenarios?.modeledExit ?? undefined;
        const editedExit = form.value.exitDate ?? undefined;
        let movedDirection: "earlier" | "later" | null = null;
        if (editedExit && algorithmExit && editedExit !== algorithmExit) {
            movedDirection = editedExit < algorithmExit ? "earlier" : "later";
        }

        return {
            algorithmBase,
            suggestedBase: editedBase,
            baseChanged,
            previewLow: scaleMaybe(result.scenarios?.low?.discountMultiple ?? undefined),
            previewHigh: scaleMaybe(result.scenarios?.high?.discountMultiple ?? undefined),
            algorithmExit,
            suggestedExit: editedExit,
            movedDirection,
            rationale: result.agentNarrative?.suggestion?.rationale ?? undefined,
            // Defined by the server for every suggestion; default to material (show the amendment) only
            // as a fail-safe for pre-change data that predates the flag.
            material: result.agentNarrative?.suggestion?.material ?? true
        };
    }

    /** Applies the (edited) suggestion via the SUGGESTION accept path. No-op if the form is invalid. */
    acceptSuggestion(result: AlgorithmicDmvResult) {
        const form = this.suggestionForm(result);
        if (!form || form.invalid) return;
        this.store.dispatch(AlgorithmicDmvActions.acceptResult({
            resultId: result.resultId,
            request: {
                acceptMethod: AlgorithmicDmvAcceptMethod.Individual,
                acceptValues: AlgorithmicDmvAppliedValues.Suggestion,
                appliedBaseMultiple: form.value.baseMultiple ?? undefined,
                appliedExitDate: form.value.exitDate ?? undefined
            },
            taskInfoId: this.taskInfoId
        }));
    }

    /** The four narrative lists, in display order, skipping any that are empty. */
    narrativeSections(narrative: AlgorithmicDmvAgentNarrative): NarrativeSection[] {
        const sections: NarrativeSection[] = [];
        const add = (label: string, items?: string[]) => {
            if (items && items.length > 0) sections.push({label, items});
        };
        add("Position trajectory", narrative.positionTrajectory);
        add("GP signal", narrative.gpSignal);
        add("Industry & market", narrative.industryMarketColor);
        add("What this means", narrative.whatThisMeans);
        return sections;
    }
}
